import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  AlertTriangle, 
  Clock, 
  CheckCircle, 
  TrendingDown, 
  Printer,
  FileText, 
  Zap, 
  Shield, 
  Wifi, 
  Wrench,
  Euro,
  Calendar,
  Bell,
  Download,
  Home
} from 'lucide-react';
import { masterSupabase } from '../lib/supabase-master';
import { useAuth } from '../contexts/AuthContext';
import EnergyComparisonWidget from '../components/EnergyComparisonWidget';
import CommercialReadinessPanel from '../components/CommercialReadinessPanel';
import { capitalFetch } from '../services/capitalApiService.js';

const ContractManagementPage = () => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState({ totalContracts: null, urgentCancellations: null, totalMonthlyCost: null, totalAnnualSavingsPotential: null });
  const [commercialReadiness, setCommercialReadiness] = useState(null);
  const { organizationId } = useAuth();

  // Capital Atlas API Basis-URL
  const CAPITAL_API = 'https://clara360.de/api/capital/trpc';

  const [vaultDocs, setVaultDocs] = useState({});
  const [loadingDocs, setLoadingDocs] = useState({});
  const [vaultDocFilter, setVaultDocFilter] = useState({});

  const loadVaultDocs = async (contractId, propertyAddress, propertyId, docTypeFilter) => {
    // Guard: kein leerer Kontext
    if (!propertyId || propertyId === '—') {
      setVaultDocs(prev => ({ ...prev, [contractId]: [] }));
      return;
    }
    // Cache-Key: contractId + aktiver Filter
    const cacheKey = `${contractId}__${docTypeFilter || 'all'}`;
    if (vaultDocs[cacheKey] !== undefined) {
      setVaultDocs(prev => ({ ...prev, [contractId]: vaultDocs[cacheKey] }));
      return;
    }
    setLoadingDocs(prev => ({ ...prev, [contractId]: true }));
    try {
      // Capital tRPC vaultDocuments.byProperty — kein hartes Limit, mit org_id-Guard
      const queryInput = {
        property_id: propertyId,
        organization_id: organizationId || undefined,
        limit: 500,  // alle Dokumente laden
        offset: 0,
      };
      if (docTypeFilter && docTypeFilter !== 'all') {
        queryInput.document_type = docTypeFilter;
      }
      const data = await capitalQuery('vaultDocuments.byProperty', queryInput);
      const docs = data?.documents || [];
      setVaultDocs(prev => ({ ...prev, [contractId]: docs, [cacheKey]: docs }));
    } catch (_e) {
      setVaultDocs(prev => ({ ...prev, [contractId]: [] }));
    } finally {
      setLoadingDocs(prev => ({ ...prev, [contractId]: false }));
    }
  };


  // Capital tRPC-Aufruf Helper
  const capitalQuery = async (endpoint, input, requestOptions = {}) => {
    const url = `${CAPITAL_API}/${endpoint}?batch=1&input=${encodeURIComponent(JSON.stringify({ '0': { json: input } }))}`;
    const res = await capitalFetch(url, requestOptions);
    if (!res.ok) throw new Error(`Capital API ${endpoint}: ${res.status}`);
    const raw = await res.json();
    return raw[0]?.result?.data?.json;
  };

  // Filter states
  const [selectedOwner, setSelectedOwner] = useState('all');
  const [selectedProperty, setSelectedProperty] = useState('all');
  const [activeContractTab, setActiveContractTab] = useState('all');

  const openEnergyInsight = (contract) => {
    // Interne Navigation only: no tracking, no partner request, no redirect.
    if (!contract || contract.category !== 'energy' || !contract.propertyId) return;
    setSelectedProperty(contract.propertyId);
    setActiveContractTab('energy');
  };
  const [owners, setOwners] = useState([]);
  const [properties, setProperties] = useState([]);

  // Lease Contracts (Mietverträge)
  const [leaseContracts, setLeaseContracts] = useState([]);
  const [leaseFilter, setLeaseFilter] = useState('all'); // all, active, terminated, expiring

  // Contract Categories mit Icons
  const contractCategories = {
    energy: { icon: Zap, label: 'Energie', color: 'bg-yellow-500' },
    insurance: { icon: Shield, label: 'Versicherung', color: 'bg-blue-500' },
    telecom: { icon: Wifi, label: 'Telekom', color: 'bg-purple-500' },
    maintenance: { icon: Wrench, label: 'Wartung', color: 'bg-green-500' },
    other: { icon: FileText, label: 'Sonstiges', color: 'bg-gray-500' }
  };

  // Fristen-Ampel Logik mit Kündigungsdeadline
  const getFristenStatus = (contract) => {
    const today = new Date();
    // NULL end_date = unbefristeter/laufender Vertrag
    if (!contract.end_date) return {
      status: 'ongoing',
      color: 'bg-green-500',
      text: 'Laufend',
      urgency: 'LAUFEND',
      canCancel: false,
      deadline: null
    };
    const endDate = new Date(contract.end_date);
    const noticePeriod = contract.cancellation_notice_period || 30;
    const cancellationDeadline = new Date(endDate);
    cancellationDeadline.setDate(cancellationDeadline.getDate() - noticePeriod);
    
    const daysUntilDeadline = Math.ceil((cancellationDeadline - today) / (1000 * 60 * 60 * 24));
    const daysUntilEnd = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntilEnd < 0) return { 
      status: 'expired', 
      color: 'bg-red-600', 
      text: 'Abgelaufen', 
      urgency: 'EXPIRED',
      canCancel: false,
      deadline: null
    };
    
    if (daysUntilDeadline <= 0) return { 
      status: 'missed', 
      color: 'bg-red-500', 
      text: 'Frist verpasst', 
      urgency: 'VERPASST',
      canCancel: false,
      deadline: cancellationDeadline
    };
    
    if (daysUntilDeadline <= 30) return { 
      status: 'critical', 
      color: 'bg-red-500', 
      text: `${daysUntilDeadline} Tage`, 
      urgency: 'DRINGEND',
      canCancel: true,
      deadline: cancellationDeadline
    };
    
    if (daysUntilDeadline <= 90) return { 
      status: 'warning', 
      color: 'bg-yellow-500', 
      text: `${daysUntilDeadline} Tage`, 
      urgency: 'BALD',
      canCancel: true,
      deadline: cancellationDeadline
    };
    
    return { 
      status: 'safe', 
      color: 'bg-green-500', 
      text: `${daysUntilDeadline} Tage`, 
      urgency: 'OK',
      canCancel: true,
      deadline: cancellationDeadline
    };
  };

  // PDF-Kündigungsschreiben Generator
  const generateCancellationPDF = async (contract) => {
    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF();
    
    const today = new Date().toLocaleDateString('de-DE');
    const fristenInfo = getFristenStatus(contract);
    
    // PDF Content
    doc.setFontSize(12);
    doc.text('Kündigung des Vertrages', 20, 30);
    doc.text(`Vertragsnummer: ${contract.contract_number || 'N/A'}`, 20, 50);
    doc.text(`Anbieter: ${contract.provider}`, 20, 60);
    doc.text(`Vertragsart: ${contractCategories[contract.category]?.label}`, 20, 70);
    doc.text(`Datum: ${today}`, 20, 90);
    
    doc.text('Sehr geehrte Damen und Herren,', 20, 110);
    doc.text(`hiermit kündige ich den oben genannten Vertrag fristgerecht zum ${new Date(contract.end_date).toLocaleDateString('de-DE')}.`, 20, 130);
    doc.text('Bitte bestätigen Sie mir die Kündigung schriftlich.', 20, 150);
    doc.text('Mit freundlichen Grüßen', 20, 180);
    
    // Download PDF
    doc.save(`Kuendigung_${contract.provider}_${today}.pdf`);
    
    // Track cancellation in database
    await masterSupabase
      .from('contract_cancellations')
      .insert({
        contract_id: contract.id,
        cancellation_date: today,
        cancellation_reason: 'Kostenoptimierung',
        status: 'pdf_generated'
      });
  };

  // Einsparpotenzial: nur aus Capital-Daten, kein Schätzwert
  const calculateSavings = (contract) => {
    return contract.capital_savings || null;
  };

  // Contract Data laden — Capital Atlas contracts.portfolio
  useEffect(() => {
    if (organizationId) {
      loadContracts();
      loadCommercialReadiness();
    } else {
      setCommercialReadiness(null);
      setLoading(false);
    }
    loadOwnersAndProperties();
    loadLeaseContracts();
  }, [organizationId]);

  const loadOwnersAndProperties = async () => {
    try {
      // Load owners
      const { data: ownersData } = await masterSupabase
        .from('owners')
        .select('id, name')
        .order('name');
      
      if (ownersData) setOwners(ownersData);

      // Load properties
      const { data: propertiesData } = await masterSupabase
        .from('properties')
        .select('id, property_name, owner_id')
        .order('property_name');
      
      if (propertiesData) setProperties(propertiesData);
    } catch (error) {
      console.error('Error loading owners/properties:', error);
    }
  };

  const loadContracts = async () => {
    if (!organizationId) return;
    try {
      // Capital Atlas: contracts.portfolio — kanonische Vertragsquelle
      const [portfolioData, kpisData] = await Promise.all([
        capitalQuery('contracts.portfolio', { organizationId }),
        capitalQuery('contracts.kpis', { organizationId }),
      ]);

      const contractsData = portfolioData?.contracts || [];

      // Feldnamen auf ContractCard-Schema mappen
      const mappedContracts = contractsData.map(c => ({
        ...c,
        id: c.id,
        name: c.providerName || c.subtype || 'Unbekannter Vertrag',
        provider: c.providerName,
        monthly_cost: c.monthlyCost,
        property_address: c.propertyName || c.propertyId || '—',
        end_date: c.endDate,
        cancellation_notice_period: 30,
        capital_savings: c.savingsOpportunity?.annualSavings || null,
        capital_savings_is_estimate: c.savingsOpportunity?.isInlineEstimate === true,
        capital_savings_source: c.savingsOpportunity?.source || null,
        capital_savings_benchmark_monthly: c.savingsOpportunity?.benchmarkMonthly ?? null,
        energy_type: c.energyType || null,
        contract_number: c.contractNumber || null,
      }));

      setContracts(mappedContracts);
      // Canonical KPI only: no client-side aggregation of indicative contract estimates.
      setKpis(kpisData || {});

    } catch (error) {
      console.error('[ContractManagement] Capital Atlas Fehler:', error);
      setContracts([]);
    } finally {
      setLoading(false);
    }
  };

  const loadCommercialReadiness = async () => {
    if (!organizationId) return;
    try {
      const data = await capitalQuery('commercialReadiness.byOrganization', { organizationId }, { organizationId });
      setCommercialReadiness(data || null);
    } catch (error) {
      // The normal contract portfolio remains available if this read-only panel is unavailable.
      console.error('[ContractManagement] Commercial Readiness nicht verfügbar:', error);
      setCommercialReadiness(null);
    }
  };
  // Load Lease Contracts (Mietverträge) - Dummy Data for now
  const loadLeaseContracts = () => {
    const dummyLeases = [
      {
        id: 1,
        tenant_name: 'Familie Müller',
        property_address: 'Waldhofstraße 76, Wohnung 3, 68169 Mannheim',
        contract_type: 'residential', // Wohnraum
        monthly_rent: 850.00,
        additional_costs: 180.00,
        start_date: '2022-04-01',
        end_date: null, // unbefristet
        status: 'active',
        notice_period: 90, // 3 Monate
        deposit: 2550.00, // 3 Monatsmieten
        signature_status: 'signed'
      },
      {
        id: 2,
        tenant_name: 'Schmidt GmbH',
        property_address: 'Bürgermeister-Fuchs-Str. 17, Gewerbeeinheit EG, 68167 Mannheim',
        contract_type: 'commercial', // Gewerbe
        monthly_rent: 2400.00,
        additional_costs: 450.00,
        start_date: '2020-01-01',
        end_date: '2025-12-31',
        status: 'active',
        notice_period: 180, // 6 Monate
        deposit: 7200.00,
        signature_status: 'signed'
      },
      {
        id: 3,
        tenant_name: 'Herr Weber',
        property_address: 'Scheffelstraße 10, Wohnung 2, 68549 Ilvesheim',
        contract_type: 'residential',
        monthly_rent: 720.00,
        additional_costs: 150.00,
        start_date: '2018-07-01',
        end_date: null,
        status: 'terminated', // Gekündigt
        termination_date: '2025-03-31',
        notice_period: 90,
        deposit: 2160.00,
        signature_status: 'signed'
      }
    ];
    setLeaseContracts(dummyLeases);
  };

  // PDF Generator für Mietverträge
  const generateLeaseContractPDF = async (lease) => {
    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF();
    const today = new Date().toLocaleDateString('de-DE');

    // Header
    doc.setFontSize(18);
    doc.text('Mietvertrag', 20, 20);
    doc.setFontSize(12);
    doc.text(`Datum: ${today}`, 150, 20);

    // Parteien
    doc.setFontSize(14);
    doc.text('1. Vertragsparteien', 20, 40);
    doc.setFontSize(10);
    doc.text(`Vermieter: H::M Projekt-Management GmbH & Co.KG`, 20, 50);
    doc.text(`Mieter: ${lease.tenant_name}`, 20, 60);

    // Mietobjekt
    doc.setFontSize(14);
    doc.text('2. Mietobjekt', 20, 80);
    doc.setFontSize(10);
    doc.text(`Anschrift: ${lease.property_address}`, 20, 90);
    doc.text(`Art: ${lease.contract_type === 'residential' ? 'Wohnraum' : 'Gewerbe'}`, 20, 100);

    // Miete und Nebenkosten
    doc.setFontSize(14);
    doc.text('3. Miete und Nebenkosten', 20, 120);
    doc.setFontSize(10);
    doc.text(`Kaltmiete: ${lease.monthly_rent.toFixed(2)} €`, 20, 130);
    doc.text(`Nebenkosten: ${lease.additional_costs.toFixed(2)} €`, 20, 140);
    doc.text(`Gesamtmiete: ${(lease.monthly_rent + lease.additional_costs).toFixed(2)} €`, 20, 150);

    // Mietdauer
    doc.setFontSize(14);
    doc.text('4. Mietdauer und Kündigung', 20, 170);
    doc.setFontSize(10);
    doc.text(`Beginn: ${new Date(lease.start_date).toLocaleDateString('de-DE')}`, 20, 180);
    doc.text(`Ende: ${lease.end_date ? new Date(lease.end_date).toLocaleDateString('de-DE') : 'Unbefristet'}`, 20, 190);
    doc.text(`Kündigungsfrist: ${lease.notice_period} Tage`, 20, 200);

    // Kaution
    doc.setFontSize(14);
    doc.text('5. Kaution', 20, 220);
    doc.setFontSize(10);
    doc.text(`Die Kaution beträgt: ${lease.deposit.toFixed(2)} €`, 20, 230);

    // Unterschriften
    doc.setFontSize(12);
    doc.text('_________________________', 20, 270);
    doc.text('Vermieter', 20, 275);
    doc.text('_________________________', 130, 270);
    doc.text('Mieter', 130, 275);

    // Download
    doc.save(`Mietvertrag_${lease.tenant_name.replace(/ /g, '_')}_${today}.pdf`);
  };

  // One-Click Kündigung + Ersatzangebot
  const handleCancelAndReplace = (contract) => {
    const savings = calculateSavings(contract);
    // Hier würde die echte Kündigungs- und Wechsel-Logik implementiert
    alert(`Kündigung für ${contract.name} vorbereitet!\n${contract.capital_savings_is_estimate ? 'Kostenprüfung erforderlich: Der Referenzwertvergleich ist keine belastbare Ersparnis.' : `Potenzielle Ersparnis: ${savings}€/Jahr`}\n\nNächste Schritte:\n1. Kündigungsschreiben generieren\n2. Günstigere Alternativen anzeigen\n3. Wechsel durchführen`);
  };

  // Contract Card Component
  const ContractCard = ({ contract }) => {
    const fristenStatus = getFristenStatus(contract);
    const savings = calculateSavings(contract);
    const CategoryIcon = contractCategories[contract.category]?.icon || FileText;
    
    return (
      <Card className="hover:shadow-lg transition-shadow duration-200">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg ${contractCategories[contract.category]?.color || 'bg-gray-500'}`}>
                <CategoryIcon className="h-5 w-5 text-white" />
              </div>
              <div>
                <CardTitle className="text-lg">{contract.name || contract.providerName || '—'}</CardTitle>
                <p className="text-sm text-gray-600 dark:text-gray-400">{contract.provider || contract.subtype || ''}</p>
              </div>
            </div>
            <Badge className={`${fristenStatus.color} text-white`}>
              {fristenStatus.urgency}
            </Badge>
          </div>
        </CardHeader>
        
        <CardContent>
          <div className="space-y-4">
            {/* Kosten & Laufzeit */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Monatliche Kosten</p>
                <p className="text-xl font-bold text-green-600">{contract.monthly_cost != null ? contract.monthly_cost.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '€' : '—'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Vertragsende</p>
                <p className="text-lg font-semibold">{fristenStatus.text}</p>
              </div>
            </div>
            
            {/* Immobilie */}
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Immobilie</p>
              <p className="text-sm font-medium">{contract.property_address}</p>
              {(contract.energy_type || contract.contract_number) && (
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                  {contract.energy_type && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {({'district_heating': 'Fernwärme', 'electricity': 'Strom', 'gas': 'Gas', 'water': 'Wasser', 'heat': 'Wärme', 'mixed_utility': 'Sammelvertrag'})[contract.energy_type] || contract.energy_type}
                    </span>
                  )}
                  {contract.contract_number && (
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      Vertragsnr.&nbsp;{contract.contract_number}
                    </span>
                  )}
                </div>
              )}
            </div>
            
            {/* Einsparpotential — nur aus Capital-Daten; Pauschalvergleiche zeigen „Kostenprüfung erforderlich" statt eines Sparbetrags */}
            {savings != null && (
              <div className={contract.capital_savings_is_estimate ? "bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg" : "bg-green-50 dark:bg-green-900/20 p-3 rounded-lg"}>
                <div className="flex items-center justify-between">
                  <div>
                    {contract.capital_savings_is_estimate ? (
                      <>
                        <p className="text-sm text-amber-700 dark:text-amber-300">Kostenprüfung erforderlich</p>
                        <p className="text-lg font-bold text-amber-600">{contract.monthly_cost != null ? `${contract.monthly_cost.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €/Monat` : 'Vertragskosten nicht belegt'}</p>
                        <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                          Tatsächliche Vertragskosten ohne vertragsgenauen Verbrauchsnachweis. Jahresabrechnung zuordnen, damit Clara belastbar rechnen kann — der Referenzwertvergleich wird intern nur als Prüfhinweis geführt.
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-sm text-green-700 dark:text-green-300">Einsparpotential</p>
                        <p className="text-lg font-bold text-green-600">{savings.toLocaleString('de-DE', { maximumFractionDigits: 0 })}€/Jahr</p>
                      </>
                    )}
                  </div>
                  <TrendingDown className={`h-6 w-6 ${contract.capital_savings_is_estimate ? "text-amber-600" : "text-green-600"}`} />
                </div>
              </div>
            )}
            
            {/* Action Buttons */}
            <div className="flex space-x-2">
              {fristenStatus.canCancel ? (
                <>
                  <Button 
                    onClick={() => generateCancellationPDF(contract)}
                    variant="outline"
                    className="flex-1"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    PDF Kündigung
                  </Button>
                </>
              ) : fristenStatus.urgency === 'LAUFEND' ? (
                <Button 
                  disabled
                  variant="outline"
                  className="flex-1 opacity-50"
                >
                  <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                  Laufend · Keine Frist
                </Button>
              ) : (
                <Button 
                  disabled
                  variant="outline"
                  className="flex-1"
                >
                  <Clock className="h-4 w-4 mr-2" />
                  {fristenStatus.urgency === 'EXPIRED' ? 'Abgelaufen' : 'Frist verpasst'}
                </Button>
              )}
              {contract.category === 'energy' && contract.propertyId && (
                <Button
                  type="button"
                  onClick={() => openEnergyInsight(contract)}
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  title="Energie-Insight öffnen"
                >
                  <Zap className="h-4 w-4 mr-1" />
                  Energie-Insight
                </Button>
              )}
            </div>
          </div>
        </CardContent>
            {/* Vault-Dokumente */}
            <div className="border-t pt-3 mt-2">
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  onClick={() => loadVaultDocs(contract.id, contract.property_address, contract.propertyId, vaultDocFilter[contract.id])}
                >
                  {loadingDocs[contract.id]
                    ? 'Lade Dokumente...'
                    : vaultDocs[contract.id] !== undefined
                      ? (vaultDocs[contract.id].length + ' Dokument(e) im Vault')
                      : 'Dokumente anzeigen'}
                </button>
                {/* Typ-Filter */}
                {vaultDocs[contract.id] !== undefined && (
                  <select
                    className="text-xs border rounded px-1 py-0.5 bg-white dark:bg-slate-900 text-gray-700 dark:text-gray-300"
                    value={vaultDocFilter[contract.id] || 'all'}
                    onChange={e => {
                      const f = e.target.value;
                      setVaultDocFilter(prev => ({ ...prev, [contract.id]: f }));
                      loadVaultDocs(contract.id, contract.property_address, contract.propertyId, f);
                    }}
                  >
                    <option value="all">Alle Typen</option>
                    <option value="mietvertrag">Mietverträge</option>
                    <option value="nk_abrechnung">NK-Abrechnungen</option>
                    <option value="rechnung">Rechnungen</option>
                    <option value="vertrag">Verträge</option>
                    <option value="kontoauszug">Kontoauszüge</option>
                    <option value="versicherung">Versicherungen</option>
                    <option value="grundbuchauszug">Grundbuch</option>
                    <option value="kaufvertrag">Kaufvertrag</option>
                    <option value="sonstiges">Sonstiges</option>
                  </select>
                )}
              </div>
              {vaultDocs[contract.id] && vaultDocs[contract.id].length > 0 && (
                <div className="mt-2 space-y-1 max-h-64 overflow-y-auto">
                  {vaultDocs[contract.id].map((doc, idx) => (
                    <div key={idx} className="flex items-center justify-between text-xs bg-gray-50 dark:bg-slate-900 rounded p-1.5 gap-2">
                      <span className="truncate text-gray-700 dark:text-gray-300 flex-1" title={doc.display_label || doc.filename}>
                        {doc.display_label || doc.filename}
                      </span>
                      <span className="shrink-0 text-gray-400">
                        {doc.document_date ? new Date(doc.document_date).toLocaleDateString('de-DE', {year:'numeric',month:'2-digit'}) : ''}
                      </span>
                      <span className="shrink-0 px-1.5 py-0.5 rounded text-xs bg-gray-200 dark:bg-slate-800 text-gray-600 dark:text-gray-300">
                        {doc.document_type || ''}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {vaultDocs[contract.id] && vaultDocs[contract.id].length === 0 && (
                <p className="text-xs text-gray-400 mt-1">Keine Dokumente im Vault gefunden</p>
              )}
            </div>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Lade Verträge...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-black overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 h-screen overflow-y-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
                <Printer className="w-8 h-8 mr-3 text-blue-600" />
                Vertragsmanagement
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                Verträge optimieren • Kosten sparen • Fristen im Blick
              </p>
            </div>
            <div className="flex space-x-3">
              <Button className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-900 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                <Download className="w-4 h-4 mr-2" />
                Alle Verträge exportieren
              </Button>
            </div>
          </div>
        </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Gesamte Verträge</p>
                <p className="text-2xl font-bold">{kpis.totalContracts != null ? kpis.totalContracts : (contracts.length > 0 ? contracts.length : '—')}</p>
              </div>
              <FileText className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Dringende Kündigungen</p>
                <p className="text-2xl font-bold text-red-600">{kpis.urgentCancellations != null ? kpis.urgentCancellations : '—'}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Monatliche Kosten</p>
                <p className="text-2xl font-bold">
                  {kpis.totalMonthlyCost != null ? kpis.totalMonthlyCost.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '€' : '—'}
                </p>
              </div>
              <Euro className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
      </div>

          {/* Fristen-Ampel Alert */}
          {(kpis.urgentCancellations || 0) > 0 && (
        <Card className="border-red-200 bg-red-50 dark:bg-red-900/20">
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <AlertTriangle className="h-6 w-6 text-red-600" />
              <div>
                <p className="font-semibold text-red-800 dark:text-red-200">
                  {kpis.urgentCancellations} Vertrag{kpis.urgentCancellations > 1 ? 'e' : ''} läuf{kpis.urgentCancellations > 1 ? 'en' : 't'} in den nächsten 30 Tagen aus!
                </p>
                <p className="text-sm text-red-600 dark:text-red-300">
                  Jetzt kündigen und Kosten optimieren.
                </p>
              </div>
              <Button className="ml-auto bg-red-600 hover:bg-red-700">
                <Bell className="h-4 w-4 mr-2" />
                Alle prüfen
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {commercialReadiness && (
        <div className="mt-8">
          <CommercialReadinessPanel
            readiness={commercialReadiness}
            properties={properties}
            selectedProperty={selectedProperty}
          />
        </div>
      )}
      {/* Filters */}
      <div className="mt-8 bg-white dark:bg-black rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Eigentümer
            </label>
            <select
              value={selectedOwner}
              onChange={(e) => {
                setSelectedOwner(e.target.value);
                setSelectedProperty('all'); // Reset property when owner changes
              }}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Alle Eigentümer</option>
              {owners.map(owner => (
                <option key={owner.id} value={owner.id}>{owner.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Objekt
            </label>
            <select
              value={selectedProperty}
              onChange={(e) => setSelectedProperty(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Alle Objekte</option>
              {properties
                .filter(prop => selectedOwner === 'all' || prop.owner_id === selectedOwner)
                .map(property => (
                  <option key={property.id} value={property.id}>{property.property_name}</option>
                ))}
            </select>
          </div>
        </div>
      </div>

      {/* Contract Tabs */}
      <div className="mt-8">
        <Tabs value={activeContractTab} onValueChange={setActiveContractTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-7 bg-gray-100 dark:bg-slate-900 p-1 rounded-lg">
          <TabsTrigger 
            value="all" 
            className="data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-sm transition-all duration-200"
          >
            Alle ({kpis.totalContracts ?? contracts.length})
          </TabsTrigger>
          <TabsTrigger 
            value="energy"
            className="data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-sm transition-all duration-200"
          >
            <Zap className="h-4 w-4 mr-2" />
            Energie
          </TabsTrigger>
          <TabsTrigger 
            value="insurance"
            className="data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-sm transition-all duration-200"
          >
            <Shield className="h-4 w-4 mr-2" />
            Versicherung
          </TabsTrigger>
          <TabsTrigger 
            value="telecom"
            className="data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-sm transition-all duration-200"
          >
            <Wifi className="h-4 w-4 mr-2" />
            Telekom
          </TabsTrigger>
          <TabsTrigger 
            value="maintenance"
            className="data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-sm transition-all duration-200"
          >
            <Wrench className="h-4 w-4 mr-2" />
            Wartung
          </TabsTrigger>
          <TabsTrigger 
            value="other"
            className="data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-sm transition-all duration-200"
          >
            <FileText className="h-4 w-4 mr-2" />
            Sonstiges
          </TabsTrigger>
          <TabsTrigger 
            value="leases"
            className="data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-sm transition-all duration-200"
          >
            <Home className="h-4 w-4 mr-2" />
            Mietverträge
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-6">
          {contracts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center space-y-4">
              <FileText className="h-16 w-16 text-gray-300 dark:text-gray-600" />
              <div>
                <p className="text-xl font-semibold text-gray-700 dark:text-gray-300">Noch keine Verträge erkannt</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-md">
                  Clara360 durchsucht Bankdaten, Dokumente und Zählerdaten nach bestehenden Verträgen.
                </p>
              </div>
              <div className="flex flex-wrap gap-3 justify-center mt-4">
                <Button variant="outline" className="flex items-center gap-2">
                  <Download className="h-4 w-4" />
                  Vertrag hochladen
                </Button>
                <Button variant="outline" className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Manuell hinzufügen
                </Button>
                <Button variant="outline" className="flex items-center gap-2">
                  <Zap className="h-4 w-4" />
                  Erkennung starten
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {contracts
                .filter(contract => {
                  if (selectedProperty !== 'all' && contract.propertyId !== selectedProperty) return false;
                  return true;
                })
                .map(contract => (
                  <ContractCard key={contract.id} contract={contract} />
                ))}
            </div>
          )}
        </TabsContent>

        {Object.keys(contractCategories).map(category => (
          <TabsContent key={category} value={category} className="space-y-6">
            {category === 'energy' && <EnergyComparisonWidget propertyId={selectedProperty} organizationId={organizationId} />}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {contracts
                .filter(contract => {
                  if (contract.category !== category) return false;
                  if (selectedProperty !== 'all' && contract.propertyId !== selectedProperty) return false;
                  return true;
                })
                .map(contract => (
                  <ContractCard key={contract.id} contract={contract} />
                ))}
            </div>
          </TabsContent>
        ))}

        {/* Mietverträge (Lease Contracts) Tab */}
        <TabsContent value="leases" className="space-y-6">
          {/* Lease Filters */}
          <div className="flex space-x-2 mb-4">
            <Button
              variant={leaseFilter === 'all' ? 'default' : 'outline'}
              onClick={() => setLeaseFilter('all')}
              className="flex-1"
            >
              Alle ({leaseContracts.length})
            </Button>
            <Button
              variant={leaseFilter === 'active' ? 'default' : 'outline'}
              onClick={() => setLeaseFilter('active')}
              className="flex-1"
            >
              Aktiv ({leaseContracts.filter(l => l.status === 'active').length})
            </Button>
            <Button
              variant={leaseFilter === 'terminated' ? 'default' : 'outline'}
              onClick={() => setLeaseFilter('terminated')}
              className="flex-1"
            >
              Gekündigt ({leaseContracts.filter(l => l.status === 'terminated').length})
            </Button>
            <Button
              variant={leaseFilter === 'expiring' ? 'default' : 'outline'}
              onClick={() => setLeaseFilter('expiring')}
              className="flex-1"
            >
              Auslaufend ({leaseContracts.filter(l => {
                if (!l.end_date) return false;
                const daysUntilEnd = Math.ceil((new Date(l.end_date) - new Date()) / (1000 * 60 * 60 * 24));
                return daysUntilEnd > 0 && daysUntilEnd <= 90;
              }).length})
            </Button>
          </div>

          {/* Lease Contracts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {leaseContracts
              .filter(lease => {
                if (leaseFilter === 'all') return true;
                if (leaseFilter === 'active') return lease.status === 'active';
                if (leaseFilter === 'terminated') return lease.status === 'terminated';
                if (leaseFilter === 'expiring') {
                  if (!lease.end_date) return false;
                  const daysUntilEnd = Math.ceil((new Date(lease.end_date) - new Date()) / (1000 * 60 * 60 * 24));
                  return daysUntilEnd > 0 && daysUntilEnd <= 90;
                }
                return true;
              })
              .map(lease => (
                <Card key={lease.id} className="hover:shadow-lg transition-shadow duration-200">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded-lg ${
                          lease.contract_type === 'residential' ? 'bg-blue-500' : 'bg-purple-500'
                        }`}>
                          <Home className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">{lease.tenant_name}</CardTitle>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {lease.contract_type === 'residential' ? 'Wohnraum' : 'Gewerbe'}
                          </p>
                        </div>
                      </div>
                      <Badge className={`${
                        lease.status === 'active' ? 'bg-green-500' : 'bg-red-500'
                      } text-white`}>
                        {lease.status === 'active' ? 'Aktiv' : 'Gekündigt'}
                      </Badge>
                    </div>
                  </CardHeader>
                  
                  <CardContent>
                    <div className="space-y-4">
                      {/* Miete & Nebenkosten */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Kaltmiete</p>
                          <p className="text-xl font-bold text-green-600">{lease.monthly_rent.toFixed(2)}€</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Nebenkosten</p>
                          <p className="text-lg font-semibold">{lease.additional_costs.toFixed(2)}€</p>
                        </div>
                      </div>
                      
                      {/* Immobilie */}
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Immobilie</p>
                        <p className="text-sm font-medium">{lease.property_address}</p>
                      </div>
                      
                      {/* Laufzeit */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Beginn</p>
                          <p className="text-sm font-medium">{new Date(lease.start_date).toLocaleDateString('de-DE')}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {lease.status === 'terminated' ? 'Kündigung zum' : lease.end_date ? 'Ende' : 'Laufzeit'}
                          </p>
                          <p className="text-sm font-medium">
                            {lease.status === 'terminated' && lease.termination_date 
                              ? new Date(lease.termination_date).toLocaleDateString('de-DE')
                              : lease.end_date 
                              ? new Date(lease.end_date).toLocaleDateString('de-DE')
                              : 'Unbefristet'
                            }
                          </p>
                        </div>
                      </div>
                      
                      {/* Kaution */}
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Kaution</p>
                        <p className="text-sm font-medium">{lease.deposit.toFixed(2)}€</p>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex space-x-2 pt-2">
                        <Button 
                          variant="outline"
                          className="flex-1"
                          size="sm"
                        >
                          <FileText className="h-4 w-4 mr-2" />
                          Dokument anzeigen
                        </Button>
                          <Button 
                            variant="outline"
                            size="sm"
                            title="PDF erzeugen"
                            onClick={() => generateLeaseContractPDF(lease)}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
          </div>
          
          {leaseContracts.filter(lease => {
            if (leaseFilter === 'all') return true;
            if (leaseFilter === 'active') return lease.status === 'active';
            if (leaseFilter === 'terminated') return lease.status === 'terminated';
            if (leaseFilter === 'expiring') {
              if (!lease.end_date) return false;
              const daysUntilEnd = Math.ceil((new Date(lease.end_date) - new Date()) / (1000 * 60 * 60 * 24));
              return daysUntilEnd > 0 && daysUntilEnd <= 90;
            }
            return true;
          }).length === 0 && (
            <Card>
              <CardContent className="p-8 text-center">
                <Home className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">
                  Keine Mietverträge in dieser Kategorie gefunden.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
      </div>
      </div>
    </div>
  );
};

export default ContractManagementPage;
